import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { date } from '../lib/format';
import { api } from '../lib/api';
import { errorMessage } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { isAdmin } from '../lib/permissions';
import { BASIS_LABELS, type CalendarEntry, type OpenItem } from '../lib/compliance-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';
import { ComplianceReminders } from './ComplianceReminders';
import { DueDateOverrideModal } from './DueDateOverrideModal';

/**
 * UYUM TAKVİMİ ve EKSİKLER.
 *
 * ⚠⚠⚠ BU EKRANIN EN ÖNEMLİ ÖZELLİĞİ, GÖSTERMEDİĞİ ŞEY: doğrulanmamış bir son
 * gün. Muhasebecisi olmayan bir operatöre YANLIŞ bir tarih göstermek, hiç
 * göstermemekten KÖTÜDÜR — kişi takvime güvenir, kendi kontrolünü yapmaz ve
 * tarih yanlışsa cezayı yazılıma güvendiği için yer.
 *
 * Bu yüzden tarihi doğrulanmamış yükümlülükler listede GÖRÜNÜR (varlıklarını
 * bilmek gerekir) ama TARİHSİZ ve açık bir uyarıyla.
 *
 * ⚠ ELLE GİRİLEN TARİH (M8b-2) SAKLANMAZ: "elle girildi" işareti, yerine
 * geçtiği yazılım tarihi, gerekçe ve kaynak satırın kendisinde görünür.
 * Operatör bir tarihin yazılımdan mı kendisinden mi geldiğini her zaman
 * görebilmeli.
 */
export function CompliancePage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [secili, setSecili] = useState<CalendarEntry | null>(null);

  /*
   * ⚠ SORGULAR SAYFADA, `compliance-api.ts`te DEĞİL. O dosya TİP ve ETİKET
   * taşıyor; panelin uyum bilgisini nereden aldığı zaten `Dashboard.tsx`te de
   * böyle yazılmış ve iki dosyanın rolü ayrı kalmalı.
   */
  const calendar = useQuery({
    queryKey: ['compliance', 'calendar'],
    queryFn: () => api.get<CalendarEntry[]>('/compliance/calendar'),
  });
  const openItems = useQuery({
    queryKey: ['compliance', 'open-items'],
    queryFn: () => api.get<OpenItem[]>('/compliance/open-items'),
  });

  const entries = calendar.data ?? [];
  const tarihli = entries.filter((e) => e.dueDate !== null);
  const tarihsiz = entries.filter((e) => e.dueDate === null);
  const geciken = tarihli.filter((e) => e.urgency === 'OVERDUE' && !e.done);
  const etiketler = new Map(entries.map((e) => [e.kind, e.label]));

  return (
    <>
      <PageHeader
        title="Uyum takvimi"
        description="Yükümlülükler, son günleri, hatırlatmalar ve biriken işler. Tarihi doğrulanmamış kalemler tarihsiz gösterilir."
      />

      {/*
        ⚠ EKSİKLER EN ÜSTTE: bunlar yükümlülük değil BİRİKEN İŞTİR ve
        kaçırılması en kolay olanlardır (kaydetmekle işlemek ayrı eylemler).
      */}
      {(openItems.data ?? []).length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="Biriken işler"
            description="Bunlar bir son gün taşımıyor ama birikirse yükümlülüğe dönüşür."
          />
          <div className="space-y-2 px-4 pb-4">
            {(openItems.data ?? []).map((o) => (
              <p key={o.code} className="text-sm">
                <strong>{o.count}</strong> {o.label}
                {o.detail ? <span className="text-[var(--c-ink-soft)]"> — {o.detail}</span> : null}
              </p>
            ))}
          </div>
        </Card>
      )}

      {geciken.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="Süresi geçmiş"
            description="Bu kalemlerin son günü doldu ve tamamlandıklarına dair bir kayıt yok."
          />
          <div className="space-y-2 px-4 pb-4">
            {geciken.map((e) => (
              <p key={`${e.kind}-${e.periodKey}`} className="text-sm text-[var(--c-danger)]">
                {date(e.dueDate ?? '')} · {e.label} ({e.periodKey})
              </p>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader title="Son günü belli olanlar" />
        {calendar.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : calendar.isError ? (
          <ErrorState message={errorMessage(calendar.error)} />
        ) : tarihli.length === 0 ? (
          <EmptyState title="Kalem yok" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Son gün</Th>
                <Th>Yükümlülük</Th>
                <Th>Dönem</Th>
                <Th>Nereye</Th>
                <Th>Durum</Th>
                {admin && <Th> </Th>}
              </tr>
            </thead>
            <tbody>
              {tarihli.map((e) => (
                <tr key={`${e.kind}-${e.periodKey}`}>
                  <Td className="text-xs">
                    <strong>{date(e.dueDate ?? '')}</strong>
                    {e.overridden && (
                      <>
                        <br />
                        <Badge tone="warn">elle girildi</Badge>
                        {e.replacedDueDate && (
                          <>
                            <br />
                            <span className="text-[var(--c-ink-soft)]">
                              yazılımın hesabı: {date(e.replacedDueDate)}
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </Td>
                  <Td>
                    <span className="text-sm">{e.label}</span>
                    {e.evidence && (
                      <>
                        <br />
                        <span className="text-xs text-[var(--c-ink-soft)]">{e.evidence}</span>
                      </>
                    )}
                    {e.overridden && e.overrideReason && (
                      <>
                        <br />
                        <span className="text-xs text-[var(--c-ink-soft)]">
                          Gerekçe: {e.overrideReason}
                          {e.overrideSourceUrl && (
                            <>
                              {' · '}
                              <a
                                href={e.overrideSourceUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="underline"
                              >
                                kaynak
                              </a>
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </Td>
                  <Td className="font-mono text-xs">{e.periodKey}</Td>
                  <Td className="text-xs">{e.channel}</Td>
                  <Td>{durumRozeti(e)}</Td>
                  {admin && (
                    <Td>
                      <Button variant="ghost" size="sm" onClick={() => setSecili(e)}>
                        Tarihi değiştir
                      </Button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/*
        ⚠⚠ TARİHİ OLMAYANLAR AYRI BİR KARTTA VE AYRI BİR BAŞLIKLA. Aynı tabloya
        konsalardı, tarihsiz satırlar gerçek son günlerin arasına serpilir ve
        liste okunmaz hâle gelirdi. Ayrıca kalemin kendisi GİZLENMİYOR:
        bilinmeyen şey yükümlülük değil, TARİHİ.
      */}
      <Card className="mt-4">
        <CardHeader
          title="Son günü YAZILIMDA OLMAYAN yükümlülükler"
          description="Bunların varlığı biliniyor, tarihleri doğrulanmadı. Yazılım tarih uydurmaz — kendiniz doğrulayıp girin; girilen tarih hatırlatma da alır."
        />
        {tarihsiz.length === 0 ? (
          <EmptyState title="Yok" />
        ) : (
          <div className="divide-y divide-[var(--c-border)]">
            {tarihsiz.map((e) => (
              <div key={`${e.kind}-${e.periodKey}`} className="space-y-1 px-4 py-3">
                <p className="text-sm font-medium">
                  {e.label}{' '}
                  <span className="text-xs text-[var(--c-ink-soft)]">
                    ({e.periodKey} · {e.channel})
                  </span>
                </p>
                <Badge tone="warn">{BASIS_LABELS[e.basis]}</Badge>
                <p className="text-xs text-[var(--c-ink-soft)]">{e.note}</p>
                {admin && (
                  <Button variant="secondary" size="sm" onClick={() => setSecili(e)}>
                    Tarih gir
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <ComplianceReminders labels={etiketler} />

      {secili && (
        <DueDateOverrideModal
          key={`${secili.kind}-${secili.periodKey}`}
          entry={secili}
          onClose={() => setSecili(null)}
        />
      )}
    </>
  );
}

/**
 * ⚠ "YAPILDI" YALNIZ KANITLA. Hazırlanmış bir beyan tamamlanmış SAYILMAZ:
 * dosyayı üretmek beyanı vermek değildir.
 */
function durumRozeti(e: CalendarEntry) {
  if (e.done) return <Badge tone="ok">Yapıldı</Badge>;
  if (e.urgency === 'OVERDUE') return <Badge tone="danger">Süresi geçti</Badge>;
  if (e.urgency === 'DUE_SOON') return <Badge tone="warn">Yaklaşıyor</Badge>;
  return <Badge tone="neutral">İleride</Badge>;
}
